import { useContext, useEffect, useRef, useState } from 'react'
import { QueryClient, QueryClientContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { agentSettingsApi, researcherApi, salesTeamApi } from '../../api/workbench'
import { useSpoke } from '../../hooks/useSpoke'
import { RefusedError } from '../../api/client'
import type { AgentState, AgentTurn } from '../../types/workbench'
import { formatDate } from '../../lib/format'
import { work } from '../../lib/working'
import { AGENT_ICON_BY_KEY, AGENT_ICON_CATALOGUE, AGENT_OWN_MARK, useGaze } from './agentIcons'
import { CommsTree, ResearcherList, StrategyHoneycomb } from './agentFindings'

// YOUR SALES TEAM (owner, 2026-10-12, B–F). Four agents under the email in draft. The Wordsmith is the conversation that writes
// the email (Chat.tsx); the three here — Researcher, Comms, Strategy — each have a chat and a set of results, kept PER
// OPPORTUNITY, so a later message on the same opportunity brings the history forward. Each answer may change the results;
// every result set is kept and the rep steps back and forward through them. A finding is APPLIED to the email as a
// Wordsmith instruction ("Use this in the email: …"); nothing here writes the email. The icon animates subtly; hover says what
// the agent does; click opens its settings for this sitting (kept in this browser) — the all-sessions settings are the voice
// profile's own knobs, reached from the Wordsmith's settings.

export const AGENT_ORDER = ['wordsmith', 'researcher', 'comms', 'strategy'] as const
export type AgentId = (typeof AGENT_ORDER)[number]

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))

/** The agent's mark (owner, 2026-10-13, after Grok Bot): a character from the catalogue — the agent's own until the person picks another —
 *  with three states. Idle it breathes, blinks and glances (calm and slightly curious); WORKING its eyes narrow to a scanning bar, the body
 *  bobs and the ring turns (it kicks into gear); the moment the work lands it settles with one small bounce. A still mark does none of it. */
export function AgentIcon({ agent, size = 36, icon, busy = false }: { agent: AgentId | string; size?: number; /** a mark from the catalogue (agentIcons.tsx); none for the agent's own */ icon?: string | null; /** the agent is working right now */ busy?: boolean }) {
  const key = icon && AGENT_ICON_BY_KEY.has(icon) ? icon : (AGENT_OWN_MARK[agent] ?? 'orb')
  const mark = AGENT_ICON_BY_KEY.get(key)!
  const live = mark.kind === 'live'
  // a LIVE mark watches the pointer: the span is the thing the gaze moves, its pupils read the two variables it sets
  const gaze = useGaze<HTMLSpanElement>(live)
  // "settles once the work is done": one beat of settle when busy drops
  const [settling, setSettling] = useState(false)
  const wasBusy = useRef(busy)
  useEffect(() => {
    if (wasBusy.current && !busy) { setSettling(true); const handle = setTimeout(() => setSettling(false), 900); wasBusy.current = busy; return () => clearTimeout(handle) }
    wasBusy.current = busy
  }, [busy])
  return (
    <span ref={gaze} className={`agent-avatar agent-${agent} ${mark.kind} look-${key}${live && busy ? ' working' : ''}${settling ? ' settle' : ''}`} style={{ width: size, height: size }} aria-hidden="true" title={mark.name}>
      <i className="agent-halo" />
      {live && <i className="agent-orbit" />}
      <svg className={`agent-icon agent-${agent}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        {mark.glyph}
      </svg>
    </span>
  )
}

export const AGENT_WORDS: Record<AgentId, { name: string; explain: string }> = {
  wordsmith: { name: 'Wordsmith', explain: 'Your Wordsmith agent helps you craft the outbound communication. Say what to change and the draft follows; it is the only agent that writes the email.' },
  researcher: { name: 'Researcher', explain: 'Your Researcher agent finds what is relevant about the company and the person — online and in the connected sources — and names the source of every finding.' },
  comms: { name: 'Comms', explain: 'Your Comms agent finds the communications you have had with this person that bear on the opportunity: threads, meetings, notes.' },
  strategy: { name: 'Strategy', explain: 'Your Strategy agent opines on the email in progress: the sales tactics it employs, how those fared in your earlier communications, and what the methodology on file says about them.' },
}

/** The three-word brief under each agent's name on its frame. */
export const AGENT_BRIEF: Record<AgentId, string> = { wordsmith: 'writes the email', researcher: 'finds what matters', comms: 'remembers every exchange', strategy: 'judges the play' }

/** Per-sitting settings for an agent, kept in this browser only (session storage); the standing instruction rides on every question. */
export interface AgentSettings { instruction: string }
const SETTINGS_KEY = (agent: string) => `scout.agent.${agent}`
export function readSettings(agent: string): AgentSettings {
  try { const raw = globalThis.sessionStorage?.getItem(SETTINGS_KEY(agent)); return raw ? (JSON.parse(raw) as AgentSettings) : { instruction: '' } } catch { return { instruction: '' } }
}
export function writeSettings(agent: string, s: AgentSettings) {
  try { globalThis.sessionStorage?.setItem(SETTINGS_KEY(agent), JSON.stringify(s)) } catch { /* no storage: the setting lives for this render only */ }
}

// a head drawn with no query client around it (a static render, a test) reads no file: the agent wears its own mark
const standalone = new QueryClient({ defaultOptions: { queries: { enabled: false, retry: false } } })

/** The mark the person gave the agent, from their file; null for its own. One read per agent, shared by every head and the picker. */
export function useAgentSettings(agent: string) {
  const spoke = useSpoke()
  const client = useContext(QueryClientContext)
  return useQuery({ queryKey: ['agent-settings', spoke, agent], queryFn: () => agentSettingsApi.get(spoke, agent), staleTime: 5 * 60_000, retry: false, enabled: !!client }, client ?? standalone)
}

/** The agent's name with its mark: hover explains, click opens the settings. */
export function AgentHead({ agent, onSettings, open, busy = false }: { agent: AgentId; onSettings?: () => void; open?: boolean; /** the agent is working right now: its mark kicks into gear */ busy?: boolean }) {
  const w = AGENT_WORDS[agent]
  const settings = useAgentSettings(agent)
  return (
    <button type="button" className={`agent-head${open ? ' on' : ''}${busy ? ' busy' : ''}`} onClick={onSettings} title={`${w.explain}${onSettings ? ' Click for its settings.' : ''}`} aria-label={`${w.name} agent${busy ? ', working' : ''}: ${w.explain}`}>
      <AgentIcon agent={agent} icon={settings.data?.icon ?? null} busy={busy} />
      <span className="agent-name">{w.name}</span>
      <span className="tag agent-tag">agent</span>
    </button>
  )
}

/** The settings: one standing instruction the agent is given with every question (or, for the Wordsmith, every turn) — kept for
 *  this sitting (this browser, until you sign out) or for every sitting (on your file, by agent). */
export function AgentSettingsPanel({ agent, onClose }: { agent: AgentId; onClose: () => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const stored = useAgentSettings(agent)
  const [text, setText] = useState(() => readSettings(agent).instruction)
  const [every, setEvery] = useState(() => readSettings(agent).instruction.length === 0)
  useEffect(() => { if (stored.data && readSettings(agent).instruction.length === 0) setText(stored.data.instruction) }, [stored.data, agent])
  const keep = useMutation({
    mutationFn: (instruction: string) => agentSettingsApi.set(spoke, agent, { instruction }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['agent-settings', spoke, agent] }); onClose() },
  })
  // THE LOOK (owner, 2026-10-13): the mark the agent wears, picked from the catalogue of forty — live ones watch the pointer, still ones
  // stand still — kept on the person's file the moment it is clicked, so it follows them to every browser
  const [looks, setLooks] = useState<'live' | 'still'>(() => (stored.data?.icon && AGENT_ICON_BY_KEY.get(stored.data.icon)?.kind) || 'live')
  const wear = useMutation({
    mutationFn: (icon: string) => agentSettingsApi.set(spoke, agent, { icon }),
    onSuccess: (s) => { qc.setQueryData(['agent-settings', spoke, agent], s) },
  })
  const wearing = stored.data?.icon ?? null
  const save = () => {
    if (every) { writeSettings(agent, { instruction: '' }); keep.mutate(text.trim()) }
    else { writeSettings(agent, { instruction: text.trim() }); onClose() }
  }
  return (
    <div className="agent-settings" role="dialog" aria-label={`${AGENT_WORDS[agent].name} settings`}>
      <p className="small"><strong>{AGENT_WORDS[agent].name}.</strong> A standing instruction it is given every time{agent === 'wordsmith' ? ' it writes' : ' you ask'}.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={agent === 'wordsmith' ? 'e.g. keep every email under 120 words; never open with a question' : agent === 'researcher' ? 'e.g. prefer filings and the company site over the press' : agent === 'comms' ? 'e.g. only the last six months' : 'e.g. judge against the Challenger methodology first'} aria-label="Standing instruction" />
      <p className="agent-settings-scope small" role="radiogroup" aria-label="Where the instruction is kept">
        <label><input type="radio" name={`scope-${agent}`} checked={!every} onChange={() => setEvery(false)} /> This sitting only (this browser, until you sign out)</label>
        <label><input type="radio" name={`scope-${agent}`} checked={every} onChange={() => setEvery(true)} /> Every sitting (kept on your file)</label>
      </p>
      <p className="agent-settings-actions">
        <button type="button" className="button small primary" onClick={save} disabled={keep.isPending}>{keep.isPending ? 'Keeping…' : every ? 'Keep for every sitting' : 'Keep for this sitting'}</button>
        <button type="button" className="button small" onClick={onClose} disabled={keep.isPending}>Cancel</button>
        {agent === 'wordsmith' && <span className="muted small">The voice itself (never-say, preferences) lives under Voice.</span>}
      </p>
      {keep.isError && <p className="error small">{why(keep.error)}</p>}
      <div className="agent-look-pick" role="group" aria-label={`The mark the ${AGENT_WORDS[agent].name} wears`}>
        <p className="small agent-look-head">
          <strong>Its mark.</strong>
          <span className="agent-look-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={looks === 'live'} className={`chip-filter ${looks === 'live' ? 'on' : ''}`} onClick={() => setLooks('live')} title="Faces that watch the pointer, blink and breathe">Live</button>
            <button type="button" role="tab" aria-selected={looks === 'still'} className={`chip-filter ${looks === 'still' ? 'on' : ''}`} onClick={() => setLooks('still')} title="The plain line icons of the trade; nothing moves">Still</button>
          </span>
          <button type="button" className={`link-button small${wearing ? '' : ' on'}`} onClick={() => wear.mutate('')} disabled={wear.isPending || !wearing} title="Back to the character the agent came with">Its own mark</button>
        </p>
        <ul className="plain agent-looks" aria-label={looks === 'live' ? 'Live marks' : 'Still marks'}>
          {AGENT_ICON_CATALOGUE.filter((e) => e.kind === looks).map((e) => (
            <li key={e.key}>
              <button type="button" className={`agent-look${wearing === e.key ? ' on' : ''}`} onClick={() => wear.mutate(e.key)} disabled={wear.isPending} aria-pressed={wearing === e.key} aria-label={e.name} title={e.name}>
                <AgentIcon agent={agent} icon={e.key} size={34} />
              </button>
            </li>
          ))}
        </ul>
        {wear.isError && <p className="error small">{why(wear.error)}</p>}
      </div>
    </div>
  )
}

/** One agent's panel: the current result set with back/forward through earlier ones, each finding with "Use in the email", and the chat. */
export function AgentPanel({ spoke, companyId, state, personId, opportunityId, engagementId, onApply, busy, draftText, modelConfigured = true, personName = null, sessionKey: skey = null, who = null, here = null, onAnswered }: {
  spoke: string; companyId: string; state: AgentState; personId?: string | null; opportunityId?: string | null; engagementId?: string | null
  /** the crafting session this panel belongs to (its key, who it is about, its route): a question the rep asks an agent is the session
   *  working, and the answer is "Scout answered" — the spinner, the toast and the hour's reminder (stand-up 2026-10-14) */
  sessionKey?: string | null; who?: string | null; here?: string | null
  /** an agent answered while the rep is here: the page stamps the session as seen */
  onAnswered?: () => void
  /** the email as it stands (the Strategy agent opines on it as it changes, owner 2026-10-12 E) */
  draftText?: string | null
  modelConfigured?: boolean
  /** the person in focus, for the Comms tree's root */
  personName?: string | null
  /** a finding applied to the email: the instruction goes to the Wordsmith */
  onApply: (instruction: string) => void
  busy?: boolean
}) {
  const qc = useQueryClient()
  const agent = state.agent as AgentId
  const [at, setAt] = useState(state.results.length - 1)
  const [text, setText] = useState('')
  const [settings, setSettings] = useState(false)
  const log = useRef<HTMLDivElement>(null)
  useEffect(() => { setAt(state.results.length - 1) }, [state.results.length])
  useEffect(() => { log.current?.scrollTo({ top: log.current.scrollHeight }) }, [state.turns.length])
  const withStanding = (q: string) => { const standing = readSettings(agent).instruction; return standing ? `${q}\n\n(Standing instruction for this sitting: ${standing})` : q }
  // THE REP'S QUESTION is the session working and the answer is Scout answering (stand-up 2026-10-14: Don asked the Researcher and walked
  // away; nothing could remind him because the answer lived outside the session): the same meta the Wordsmith's turns carry, read by the
  // cache off the page — the menu's spinner, the toast when it lands wherever the rep is by then — and, when they are still here, "seen"
  const ask = useMutation({
    mutationFn: (q: string) => { if (skey) work.start(skey, who ?? state.name); return salesTeamApi.ask(spoke, companyId, agent, withStanding(q), personId, opportunityId, engagementId) },
    meta: skey ? { working: skey, label: who ?? state.name, done: `The ${state.name} answered on ${who ?? 'this account'}`, href: here ?? undefined } : undefined,
    onSuccess: () => { setText(''); void qc.invalidateQueries({ queryKey: ['sales-team', spoke, companyId] }); onAnswered?.() },
  })
  // the page's own read (the Strategy agent on a changed draft): on the chat, never the rep speaking, never a toast or a reminder
  const autoRead = useMutation({
    mutationFn: (q: string) => salesTeamApi.ask(spoke, companyId, agent, withStanding(q), personId, opportunityId, engagementId, true),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['sales-team', spoke, companyId] }) },
  })
  const lookup = useMutation({
    mutationFn: (q: string) => researcherApi.lookup(spoke, companyId, q, personId, opportunityId, engagementId),
    onSuccess: () => { setText(''); void qc.invalidateQueries({ queryKey: ['sales-team', spoke, companyId] }); void qc.invalidateQueries({ queryKey: ['workbench', spoke, companyId] }) },
  })
  // the Strategy agent opines in real time (E): when the draft changes, it is asked to read it again, a few seconds after the last change
  const opined = useRef<string | null>(null)
  useEffect(() => {
    if (agent !== 'strategy' || !modelConfigured || !draftText || draftText.trim().length < 40 || opined.current === draftText) return
    const first = opined.current === null && state.turns.length > 0
    opined.current = draftText
    if (first) return   // the history brought forward already has a read; the next change gets a fresh one
    const handle = setTimeout(() => { if (!ask.isPending && !autoRead.isPending) autoRead.mutate('(automatic read) The draft changed. Read it as it now stands: which tactics does it employ, how well, and what would you change?') }, 4000)
    return () => clearTimeout(handle)
  }, [agent, draftText, modelConfigured]) // eslint-disable-line react-hooks/exhaustive-deps
  const results = state.results[Math.max(0, Math.min(at, state.results.length - 1))] ?? []
  const send = () => { const q = text.trim(); if (q && !ask.isPending) ask.mutate(q) }
  return (
    <section className={`agent-panel agent-${agent}`} aria-label={`${state.name} agent`}>
      <div className="agent-panel-head">
        <AgentHead agent={agent} open={settings} onSettings={() => setSettings((v) => !v)} busy={ask.isPending || lookup.isPending} />
        <span className="agent-brief muted small">{AGENT_BRIEF[agent]}</span>
        {state.results.length > 1 && (
          <span className="agent-nav" role="group" aria-label="Earlier and later results">
            <button type="button" className="button small" onClick={() => setAt((i) => Math.max(0, i - 1))} disabled={at <= 0} title="Earlier results">◀</button>
            <span className="muted small">{Math.min(at, state.results.length - 1) + 1} of {state.results.length}</span>
            <button type="button" className="button small" onClick={() => setAt((i) => Math.min(state.results.length - 1, i + 1))} disabled={at >= state.results.length - 1} title="Later results">▶</button>
          </span>
        )}
      </div>
      {settings && <AgentSettingsPanel agent={agent} onClose={() => setSettings(false)} />}
      {/* each agent's findings in its own shape (owner, 2026-10-13): the Researcher's tiles, the Comms agent's tree, the Strategy agent's honeycomb */}
      {agent === 'comms'
        ? <CommsTree results={results} onApply={(f) => onApply(f.apply)} busy={busy || ask.isPending} personName={personName} />
        : agent === 'strategy'
          ? <StrategyHoneycomb results={results} onApply={(f) => onApply(f.apply)} busy={busy || ask.isPending} />
          : <ResearcherList results={results} onApply={(f) => onApply(f.apply)} busy={busy || ask.isPending} />}
      {state.turns.length > 0 && (
        <div className="agent-log chat-thread" ref={log} aria-live="polite">
          {state.turns.map((t) => <TurnLine key={t.seq} t={t} name={state.name} onApply={onApply} busy={busy || ask.isPending} />)}
        </div>
      )}
      <form className="agent-ask" onSubmit={(e) => { e.preventDefault(); send() }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Ask the ${state.name}…`} aria-label={`Ask the ${state.name}`} disabled={ask.isPending} />
        <button type="submit" className="button small primary" disabled={!text.trim() || ask.isPending || lookup.isPending}>{ask.isPending ? 'Working…' : 'Ask'}</button>
        {agent === 'researcher' && <button type="button" className="button small" disabled={ask.isPending || lookup.isPending} onClick={() => lookup.mutate(text.trim())} title="A proper look-up as a job: what is on file, then the public web, your question first; it lands in the findings when done.">{lookup.isPending ? 'Queuing…' : 'Look up properly'}</button>}
      </form>
      {lookup.isError && <p className="error small">{why(lookup.error)}</p>}
      {ask.isError && <p className="error small">{why(ask.error)}</p>}
    </section>
  )
}

/** One finding as a card: the text, where it came from, and — anchored in the card's corner — the one button that applies it to the
 *  email (owner, 2026-10-13: a button, not a tiny link, in the corner of every item that can go into the email). */
/** One turn as a message: the rep's on the right in the brand, the agent's on the left in white, the name and the time as a caption beneath.
 *  Every answer carries "Use in the email" (Chris, stand-up 2026-10-14: "a use this button to every single response bubble it finds as it's
 *  chatting to you"): the answer goes to the Wordsmith as an instruction, and the rep sends it. */
export function TurnLine({ t, name, onApply, busy = false }: { t: AgentTurn; name: string; onApply?: (instruction: string) => void; busy?: boolean }) {
  return (
    <div className={`agent-turn ${t.role}`}>
      <p className="agent-turn-text">{t.text}</p>
      <span className="muted small agent-turn-caption"><span className="agent-turn-who">{t.role === 'rep' ? 'You' : name}</span> · {formatDate(t.at)}</span>
      {t.role === 'agent' && onApply && (
        <button type="button" className="button small agent-use agent-turn-use" disabled={busy} onClick={() => onApply(`Use this in the email: ${t.text}`)} title="Hands this answer to the Wordsmith as an instruction; the draft follows">Use in the email</button>
      )}
    </div>
  )
}

export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

/** The rest of the team in one row, each in its own frame: the three agents read for this opportunity. (The Wordsmith sits beside the draft on
 *  the bench; it may still be passed in as children where a page wants all four together.) */
export function SalesTeam({ spoke, companyId, personId, opportunityId, engagementId, onApply, busy, children, draftText, personName = null, sessionKey = null, who = null, here = null, onAnswered }: {
  spoke: string; companyId: string; personId?: string | null; opportunityId?: string | null; engagementId?: string | null
  onApply: (instruction: string) => void; busy?: boolean
  /** the crafting session the row belongs to: a question to an agent is the session working, its answer is Scout answering (2026-10-14) */
  sessionKey?: string | null; who?: string | null; here?: string | null; onAnswered?: () => void
  /** the email as it stands, for the Strategy agent's live read */
  draftText?: string | null
  /** the Wordsmith's panel, first in the row */
  children?: React.ReactNode
  /** the person in focus, named at the root of the Comms tree */
  personName?: string | null
}) {
  const q = useQuery({
    queryKey: ['sales-team', spoke, companyId, personId ?? '', opportunityId ?? '', engagementId ?? ''],
    queryFn: () => salesTeamApi.view(spoke, companyId, personId, opportunityId, engagementId),
    staleTime: 30_000,
  })
  return (
    <div className="sales-team">
      {children}
      {q.isPending && <p className="muted small agent-panel">Your team is reading the file…</p>}
      {q.isError && <p className="muted small agent-panel">{why(q.error)}</p>}
      {q.data?.agents.map((a) => (
        <AgentPanel key={a.agent} spoke={spoke} companyId={companyId} state={a} personId={personId} opportunityId={opportunityId} engagementId={engagementId} onApply={onApply} busy={busy} draftText={draftText} modelConfigured={q.data?.modelConfigured ?? true} personName={personName}
          sessionKey={sessionKey} who={who} here={here} onAnswered={onAnswered} />
      ))}
      {q.data && !q.data.modelConfigured && <p className="muted small sales-team-note">No model is connected on this instance: the agents show what is on file and cannot be asked.</p>}
    </div>
  )
}
